import { Inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
    PdfViewerDesktopService,
    PdfViewerDialogInput,
} from '../../../../projects/kiene-core/src/lib/components/pdf-viewer/kiene-pdf-viewer/kiene-pdf-viewer-desktop/pdf-viewer-desktop.service';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { Bestandsbesuchprotokoll } from '../bestandsbesuchsprotokoll/bestandsbesuchsprotokoll';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneApi } from '../../components/kiene-table/kiene-api';
import { formatDate } from '@angular/common';
import { Abgabebeleg } from '../../../../projects/kiene-core/src/lib/components/abgabebelege/classes/abgabebeleg';
import { AbgabebelegPosition } from '../../../../projects/kiene-core/src/lib/components/abgabebelege/classes/abgabebeleg-position';
import { Bestandsbuch } from '../../../../projects/kiene-core/src/lib/bestandsbuecher/classes/bestandsbuch';

@Injectable()
export class AbgabebelegService {
    constructor(
        private readonly http: HttpClient,
        private readonly pdfService: PdfViewerDesktopService,
        private readonly messageService: MessageService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {}

    bestandsbesuchsprotokollErzeugen(abgabebeleg: Abgabebeleg): Observable<Bestandsbesuchprotokoll> {
        return this.http.post<Bestandsbesuchprotokoll>(
            this.apiBaseUrl + 'bestandsbesuchsprotokoll/transform.php',
            abgabebeleg,
        );
    }

    getOffeneImpfkontrollbuecher(kunde_id: number, datum: Date): Observable<KieneApi> {
        let params = new HttpParams().set('typ', '2');
        params = params.set('abschliessbar', '1');
        params = params.set('kunde_id', kunde_id);
        if (datum) {
            params = params.set('datum_max', formatDate(datum, 'yyyy-MM-dd', 'de-DE'));
        }
        return this.http?.get<KieneApi>(this.apiBaseUrl + 'bestandsbuch/read.php', { params: params });
    }

    offeneImpfbuecherAbschliessen(
        datum: Date,
        offeneImpfbuecher: Bestandsbuch[],
        tierarzt_id: number,
        force: boolean,
    ): Observable<boolean> {
        const subject = new Subject<boolean>();
        const obj = {
            datum_kontrolle: new Date(datum),
            impfkontrollbuecher: offeneImpfbuecher,
            tierarzt_id: tierarzt_id,
            force: force ? 1 : 0,
        };
        this.http?.post(this.apiBaseUrl + 'bestandsbuch/abschliessen_impfkontrollbuch.php', obj).subscribe({
            next: () => {
                this.messageService.infoMessage('Die offenen Impfkontrollbücher wurden erfolgreich abgeschlossen!');
                subject.next(true);
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
                subject.next(false);
            },
        });
        return subject;
    }

    transformToBehandlungsanfrage(abgabebeleg: Abgabebeleg): Observable<any> {
        return this.http.post(this.apiBaseUrl + 'behandlungsanfrage/transform.php', abgabebeleg);
    }

    generiereMeldungen(abgabebelegId?: number): Observable<any> {
        return this.http.post(this.apiBaseUrl + 'meldung/generieren.php', { abgabebeleg_id: abgabebelegId });
    }

    getChargenForMaterial(abgabebelegPosition: AbgabebelegPosition, tierarztId: number): Observable<KieneApi> {
        let params: HttpParams = new HttpParams();
        if (abgabebelegPosition?.aa_id) {
            params = params.append('aa_id', abgabebelegPosition?.aa_id?.toString());
        }
        if (tierarztId) {
            params = params.append('tierarzt_id', tierarztId?.toString());
        }
        params = params.append('artikel_id', abgabebelegPosition?.artikel_id?.toString());
        return this.http.get<KieneApi>(this.apiBaseUrl + 'bestand_charge/read_abgabebeleg.php', { params: params });
    }

    openAbgabebelegGesamtPdf(abgabebeleg: Abgabebeleg, kunde_id: number): Observable<boolean> {
        const input = new PdfViewerDialogInput(
            'abgabebeleg/read_or_mail_pdf_gesamt.php',
            new HttpParams().set('abgabebeleg_id', abgabebeleg?.abgabebeleg_id?.toString()),
            'Abgabebeleg gesamt ' + abgabebeleg?.abgabebeleg_nr,
            { email: { sende_mail: true, kunde_id: kunde_id } },
        );
        return this.pdfService.openPdfViewer(input);
    }

    openAbgabebelegPdf(abgabebeleg: Abgabebeleg, kunde_id: number) {
        const input = new PdfViewerDialogInput(
            'abgabebeleg/read_or_mail_pdf.php',
            new HttpParams().set('abgabebeleg_id', abgabebeleg?.abgabebeleg_id?.toString()),
            'Abgabebeleg ' + abgabebeleg?.abgabebeleg_nr,
            { email: { sende_mail: true, kunde_id: kunde_id } },
        );
        this.pdfService.openPdfViewer(input);
    }

    openImpfkontrollbuchPdf(abgabebeleg: Abgabebeleg, kunde_id: number) {
        const input = new PdfViewerDialogInput(
            'abgabebeleg/read_or_mail_pdf_impfkontrollbuch.php',
            new HttpParams().set('abgabebeleg_id', abgabebeleg?.abgabebeleg_id?.toString()),
            'Impfkontrollbuch ' + abgabebeleg.abgabebeleg_nr,
            { email: { sende_mail: true, kunde_id: kunde_id } },
        );
        this.pdfService.openPdfViewer(input);
    }

    openBestandsbuchPdf(abgabebeleg: Abgabebeleg, kunde_id: number) {
        const input = new PdfViewerDialogInput(
            'abgabebeleg/read_or_mail_pdf_bestandsbuch.php',
            new HttpParams().set('abgabebeleg_id', abgabebeleg?.abgabebeleg_id?.toString()),
            'Bestandsbuch ' + abgabebeleg?.abgabebeleg_nr,
            { email: { sende_mail: true, kunde_id: kunde_id } },
        );
        this.pdfService.openPdfViewer(input);
    }
}
